import dayjs from "dayjs";
import _ from "lodash";
import { DiagnosticReportRowData } from "../../tables/notes/diagnostic-reports-rows";
import { FeaturedCategory } from "../categories";
import { TableRow } from "../../utils/tables-metadata";

/**
 * Mapped specialty names (from specialty-utils.ts)
 */
const SPECIALTIES = {
  cardiology: ["Cardiovascular", "Thoracic Surgery (Cardiothoracic Vascular Surgery)"],
  neurology: ["Psychiatry & Neurology", "Neurological Surgery"],
  oncology: ["Medical Oncology", "Hematology"],
  pulmonology: ["Pulmonary"],
  ophthalmology: ["Ophthalmology", "Optometrist"],
  pcp: [] as string[], // ["Family Medicine", "Internal Medicine", "General Practice"], // this seems very common, so ignore for now
};

/**
 * Note categories for hospital admissions (from note-type-utils.ts)
 */
const HOSPITAL_ADMISSION_NOTE_CATEGORIES = ["ED Notes", "Discharge Summary"];

const MAX_FEATURED_NOTES = 5;

function skipTelephoneEncounters(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  return reports.filter(report => report.noteCategory !== "Telephone Encounter");
}

function get5MostRecentNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  return _(reports)
    .orderBy(report => dayjs(report.date).valueOf(), "desc")
    .take(MAX_FEATURED_NOTES)
    .value();
}

/**
 * Filter Cardiology/Cardiovascular specialist notes for Featured section
 */
export function getCardiologyNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.cardiology.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter Neurology specialist notes for Featured section
 */
export function getNeurologyNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.neurology.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter Oncology specialist notes for Featured section
 */
export function getOncologyNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.oncology.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter Pulmonology specialist notes for Featured section
 */
export function getPulmonologyNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.pulmonology.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter Ophthalmology and Optometry specialist notes for Featured section
 */
export function getOphthalmologyOptometryNotes(
  reports: DiagnosticReportRowData[]
): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.ophthalmology.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter PCP (Primary Care Provider) notes for Featured section
 */
export function getPCPNotes(reports: DiagnosticReportRowData[]): DiagnosticReportRowData[] {
  const filtered = reports.filter(report => SPECIALTIES.pcp.includes(report.specialty));
  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

/**
 * Filter recent hospital admission notes (ED or discharge) from the last year
 */
export function getRecentHospitalAdmissionNotes(
  reports: DiagnosticReportRowData[]
): DiagnosticReportRowData[] {
  const oneYearAgo = dayjs().subtract(1, "year");

  const filtered = reports.filter(report => {
    const reportDate = dayjs(report.date);
    const isOlderThanOneYear = !reportDate.isValid() || reportDate.isBefore(oneYearAgo);
    const isAmbulatory = report.originalData.getEncounter()?.class?.code === "AMB";
    if (isOlderThanOneYear || isAmbulatory) return false;

    return HOSPITAL_ADMISSION_NOTE_CATEGORIES.includes(report.noteCategory);
  });

  return get5MostRecentNotes(skipTelephoneEncounters(filtered));
}

export function getFeaturedNotes(): FeaturedCategory {
  return {
    category: "Notes",
    subCategories: [
      {
        subCategory: "Cardiology",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) => getCardiologyNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "Neurology",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) => getNeurologyNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "Oncology",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) => getOncologyNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "Pulmonology",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) => getPulmonologyNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "Ophthalmology & Optometry",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) =>
          getOphthalmologyOptometryNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "PCP",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) => getPCPNotes(data as DiagnosticReportRowData[]),
      },
      {
        subCategory: "Hospital Admissions (Last Year)",
        tableType: "DiagnosticReport",
        getRows: (data: TableRow[]) =>
          getRecentHospitalAdmissionNotes(data as DiagnosticReportRowData[]),
      },
    ],
  };
}
